import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
    User,
    IdentificationBadge,
    Envelope,
    Lock,
    Eye,
    EyeSlash,
    Buildings,
    MapPin,
    ChartLineUp,
    ShieldCheck,
    ChartPie,
    UsersThree,
    ShieldStar,
} from "@phosphor-icons/react";
import TotalTerminalVisionLogo from "../components/TotalTerminalVisionLogo";
import { TERMINALS } from "../lib/terminals";

const FEATURE_PILLS = [
    { icon: ChartLineUp, label: "REAL-TIME INSIGHTS" },
    { icon: ShieldCheck, label: "OPERATIONAL EXCELLENCE" },
    { icon: ChartPie, label: "DATA DRIVEN DECISIONS" },
    { icon: UsersThree, label: "TEAM COLLABORATION" },
];

export default function Login() {
    const { login } = useAuth();
    const nav = useNavigate();
    const [terminal, setTerminal] = useState("");
    const [terminalOpen, setTerminalOpen] = useState(false);
    const [username, setUsername] = useState("");
    const [email, setEmail] = useState("");
    const [role, setRole] = useState("supervisor");
    const [password, setPassword] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!terminal) {
            toast.error("Please select a terminal");
            return;
        }
        setLoading(true);
        try {
            const result = await login({
                username: username.trim(),
                password,
                role,
                terminal,
                email: email.trim() || undefined,
            });
            toast.success("Welcome back");
            const active = result?.active_terminal || result?.terminal;
            if (active !== "richards_bay") {
                nav("/coming-soon");
                return;
            }
            const r = result?.role;
            if (r === "fel_operator") { nav("/fel"); return; }
            if (r === "hr") { nav("/hr-dashboard"); return; }
            if (r === "client") { nav("/client"); return; }
            if (r === "tfr_yard" || r === "rail_coordinator" || r === "mine_rail_planner") {
                nav("/rail-operations"); return;
            }
            nav("/portal");
        } catch (err) {
            toast.error(err.message || "Sign in failed");
        } finally {
            setLoading(false);
        }
    };

    const selectedLabel = TERMINALS.find((t) => t.id === terminal)?.name || "Select a terminal";

    return (
        <div className="ttv-login-shell" data-testid="login-page">
            {/* Background layers */}
            <div className="ttv-bg-image" aria-hidden />
            <div className="ttv-bg-overlay" aria-hidden />
            <div className="ttv-bg-grid" aria-hidden />

            <div className="relative z-10 min-h-screen flex flex-col">
                {/* Main split content */}
                <div className="flex-1 grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] items-center gap-6 lg:gap-10 px-6 md:px-14 py-10">
                    {/* LEFT — Brand + tagline + feature pills */}
                    <section className="ttv-left-panel flex flex-col items-center text-center max-w-3xl mx-auto w-full py-10 lg:py-14 px-6 lg:px-10 rounded-3xl">
                        <TotalTerminalVisionLogo size="2xl" className="mx-auto" />

                        <h1 className="font-display font-black leading-[0.95] tracking-tight text-[#081A36] text-4xl md:text-5xl mt-10">
                            See Everything.<br />
                            <span className="text-[#2563EB]">Achieve More.</span>
                        </h1>
                        <div className="h-1 w-16 bg-[#2563EB] mt-5 rounded-full" />
                        <p className="text-slate-600 mt-5 text-base md:text-lg leading-relaxed max-w-lg">
                            Real-time visibility across every operation.
                            Smarter decisions. Stronger performance.
                        </p>

                        <ul className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4 w-full max-w-2xl">
                            {FEATURE_PILLS.map(({ icon: Icon, label }) => (
                                <li key={label} className="ttv-feature-tile-light" data-testid={`feature-${label.split(" ")[0].toLowerCase()}`}>
                                    <div className="ttv-feature-icon-light"><Icon size={22} weight="duotone" /></div>
                                    <div className="ttv-feature-label-light">{label}</div>
                                </li>
                            ))}
                        </ul>
                    </section>

                    {/* RIGHT — Sign in card */}
                    <section className="w-full flex justify-center lg:justify-end">
                        <div className="ttv-card" role="dialog" aria-labelledby="ttv-welcome">
                            <div className="flex flex-col items-center text-center mb-6">
                                <div className="ttv-user-badge">
                                    <User size={22} weight="duotone" />
                                </div>
                                <h2 id="ttv-welcome" className="font-display font-black text-slate-900 text-3xl mt-3">
                                    Welcome Back
                                </h2>
                                <p className="text-slate-500 text-sm mt-1.5">
                                    Sign in to access <span className="text-[#2563EB] font-semibold">Total Terminal Vision</span>
                                </p>
                            </div>
                            <div className="h-px bg-slate-200 mb-6" />

                            <form onSubmit={submit} className="space-y-4" data-testid="login-form">
                                {/* Terminal picker */}
                                <div>
                                    <label className="ttv-label">Select Terminal</label>
                                    <div className="relative">
                                        <button
                                            type="button"
                                            onClick={() => setTerminalOpen((o) => !o)}
                                            onBlur={() => setTimeout(() => setTerminalOpen(false), 150)}
                                            className={`ttv-input pl-11 pr-9 flex items-center justify-between text-left ${terminal ? "text-slate-900" : "text-slate-400"}`}
                                            data-testid="login-terminal"
                                        >
                                            <Buildings size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#2563EB]" />
                                            <span className="truncate">{selectedLabel}</span>
                                            <span className={`absolute right-3 top-1/2 -translate-y-1/2 transition ${terminalOpen ? "rotate-180" : ""}`}>▾</span>
                                        </button>
                                        {terminalOpen && (
                                            <ul className="ttv-dropdown" role="listbox" data-testid="login-terminal-menu">
                                                {TERMINALS.map((t, idx) => (
                                                    <li
                                                        key={t.id}
                                                        role="option"
                                                        aria-selected={terminal === t.id}
                                                        tabIndex={0}
                                                        onMouseDown={(e) => {
                                                            e.preventDefault();
                                                            setTerminal(t.id);
                                                            setTerminalOpen(false);
                                                        }}
                                                        className={`ttv-dropdown-item ${terminal === t.id || idx === 0 && !terminal ? "ttv-dropdown-item-active" : ""}`}
                                                        data-testid={`login-terminal-option-${t.id}`}
                                                    >
                                                        <span className="truncate">{t.name}</span>
                                                        <MapPin size={16} weight="duotone" />
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </div>

                                {/* Username */}
                                <div>
                                    <label className="ttv-label">Username</label>
                                    <div className="relative">
                                        <IdentificationBadge size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                        <input
                                            className="ttv-input pl-11"
                                            placeholder="Enter your username"
                                            value={username}
                                            onChange={(e) => setUsername(e.target.value)}
                                            required
                                            data-testid="login-username"
                                            autoComplete="username"
                                        />
                                    </div>
                                </div>

                                {/* Role */}
                                <div>
                                    <label className="ttv-label">Role</label>
                                    <div className="relative">
                                        <ShieldStar size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                        <select
                                            value={role}
                                            onChange={(e) => setRole(e.target.value)}
                                            className="ttv-input pl-11 pr-9 appearance-none"
                                            required
                                            data-testid="login-role"
                                        >
                                            <optgroup label="Operations">
                                                <option value="supervisor">Supervisor</option>
                                                <option value="weighbridge_supervisor">Weighbridge Supervisor</option>
                                                <option value="fel_operator">FEL Operator</option>
                                                <option value="tippler_operator">Tippler Operator</option>
                                                <option value="shift_millwright">Shift Millwright</option>
                                                <option value="electrician">Electrician</option>
                                            </optgroup>
                                            <optgroup label="Management">
                                                <option value="manager">Manager</option>
                                                <option value="plant_manager">Plant Manager</option>
                                                <option value="fleet_manager">Fleet Manager</option>
                                                <option value="admin">Admin</option>
                                            </optgroup>
                                            <optgroup label="Support">
                                                <option value="safety">Safety</option>
                                                <option value="client_liaison">Client Liaison</option>
                                                <option value="finance">Finance</option>
                                                <option value="hr">HR</option>
                                            </optgroup>
                                            <optgroup label="Rail Operations">
                                                <option value="rail_coordinator">Grindrod Rail Coordinator</option>
                                                <option value="tfr_yard">TFR Yard</option>
                                                <option value="mine_rail_planner">Mine Rail Planner</option>
                                            </optgroup>
                                        </select>
                                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">▾</span>
                                    </div>
                                </div>

                                {/* Email (optional) */}
                                <div>
                                    <label className="ttv-label">E-mail address <span className="text-slate-400 font-normal">(optional)</span></label>
                                    <div className="relative">
                                        <Envelope size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                        <input
                                            type="email"
                                            className="ttv-input pl-11"
                                            placeholder="Enter your e-mail address"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            data-testid="login-email"
                                            autoComplete="email"
                                        />
                                    </div>
                                </div>

                                {/* Password */}
                                <div>
                                    <label className="ttv-label">Password</label>
                                    <div className="relative">
                                        <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                        <input
                                            type={showPw ? "text" : "password"}
                                            className="ttv-input pl-11 pr-11"
                                            placeholder="Enter your password"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            required
                                            data-testid="login-password"
                                            autoComplete="current-password"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPw((s) => !s)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 transition"
                                            aria-label={showPw ? "Hide password" : "Show password"}
                                            data-testid="login-password-toggle"
                                        >
                                            {showPw ? <EyeSlash size={18} /> : <Eye size={18} />}
                                        </button>
                                    </div>
                                </div>

                                <div className="flex items-center justify-end pt-1">
                                    <Link
                                        to="/forgot-password"
                                        className="text-sm text-[#2563EB] hover:text-[#1D4ED8] font-semibold"
                                        data-testid="forgot-password-link"
                                    >
                                        Forgot Password?
                                    </Link>
                                </div>

                                <button
                                    type="submit"
                                    className="ttv-btn-primary"
                                    disabled={loading}
                                    data-testid="login-submit-button"
                                >
                                    {loading ? "Signing in…" : "Sign In"}
                                </button>

                                <div className="text-center pt-2 text-sm text-slate-500">
                                    New to Total Terminal Vision?{" "}
                                    <Link
                                        to="/register"
                                        className="text-[#2563EB] font-semibold hover:underline"
                                        data-testid="register-link"
                                    >
                                        Create an account
                                    </Link>
                                </div>
                            </form>
                        </div>
                    </section>
                </div>

                {/* Footer */}
                <footer className="px-6 md:px-14 py-4 text-slate-600 text-[11px] uppercase tracking-[0.18em] font-semibold flex flex-col md:flex-row items-center justify-between gap-2 border-t border-slate-300/60">
                    <div className="flex flex-col items-start gap-2">
                        <div>
                            <span className="text-[#081A36]">SECURE. RELIABLE. </span>
                            <span className="text-[#2563EB]">BUILT FOR TERMINALS.</span>
                        </div>
                        <Link
                            to="/engagement-survey"
                            className="ttv-download-proposal group"
                            data-testid="engagement-survey-link"
                            aria-label="Anonymous employee engagement survey"
                            style={{ background: "linear-gradient(135deg, #065F46 0%, #10B981 100%)", borderColor: "rgba(16, 185, 129, 0.35)" }}
                        >
                            <span className="ttv-download-proposal-icon" aria-hidden style={{ background: "rgba(255,255,255,0.18)" }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M9 11H5a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2h-4" />
                                    <path d="M12 15V3" />
                                    <path d="m8 7 4-4 4 4" />
                                </svg>
                            </span>
                            <span>Employee Engagement Survey <span style={{ opacity: 0.85, fontWeight: 500 }}>· Anonymous · 5 minutes</span></span>
                            <span className="ttv-download-proposal-arrow" aria-hidden style={{ color: "#A7F3D0" }}>→</span>
                        </Link>
                    </div>
                    <div className="text-slate-500 normal-case tracking-normal text-xs">
                        © {new Date().getFullYear()} Total Terminal Vision. All rights reserved.
                    </div>
                    <div>
                        <span className="text-[#081A36]">ONE PLATFORM. </span>
                        <span className="text-[#2563EB]">TOTAL VISIBILITY.</span>
                    </div>
                </footer>
            </div>
        </div>
    );
}
